import React from 'react';
import { DocumentListItem } from '../../types/document';
import { MagnifyingGlassIcon, DocumentIcon } from '@heroicons/react/24/outline';
import FilterAccordion from '../ui/FilterAccordion';
import LazyLoadTrigger from '../ui/LazyLoadTrigger';
import { useBatchedList } from '../../hooks/useBatchedList';

interface EntityDocumentsListProps {
  items: DocumentListItem[];
  onSelectEntity: (entityId: number) => void;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  loading: boolean;
}

const EntityDocumentsList: React.FC<EntityDocumentsListProps> = ({
  items,
  onSelectEntity,
  searchTerm,
  onSearchChange,
  loading,
}) => {
  const { visible, hasMore, loadMore } = useBatchedList(items, 5);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Betöltés...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Search */}
      <div className="mb-4">
        <FilterAccordion activeCount={searchTerm ? 1 : 0}>
          <div className="relative pt-2 pb-1 md:py-0">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Keresés..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
        </FilterAccordion>
      </div>

      {/* Mobile Cards (below md) */}
      <div className="md:hidden">
        {items.length === 0 ? (
          <div className="bg-white shadow rounded-lg py-8 text-center text-sm text-gray-500">
            Nincs találat
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              {visible.map((item) => (
                <button
                  type="button"
                  key={item.entityId}
                  onClick={() => onSelectEntity(item.entityId)}
                  className="w-full bg-white rounded-lg shadow border p-3 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-1.5">
                    <div className="min-w-0">
                      <div className="text-sm font-medium leading-snug text-gray-900 break-words">
                        {item.name}
                      </div>
                      {item.ageGroups && (
                        <div className="mt-0.5 text-xs text-gray-400 break-words">{item.ageGroups}</div>
                      )}
                    </div>
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-primary-100 text-primary-800 flex-shrink-0">
                      {item.documentCount}
                    </span>
                  </div>
                </button>
              ))}
            </div>
            {hasMore && <LazyLoadTrigger key={visible.length} onLoadMore={loadMore} />}
          </>
        )}
      </div>

      {/* Desktop Table (hidden below md) */}
      <div className="hidden md:block bg-white shadow overflow-hidden sm:rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Név
              </th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Dokumentumok száma
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {items.length === 0 ? (
              <tr>
                <td colSpan={2} className="px-6 py-4 text-center text-sm text-gray-500">
                  Nincs találat
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr
                  key={item.entityId}
                  onClick={() => onSelectEntity(item.entityId)}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <DocumentIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <div className="text-sm font-medium text-gray-900">{item.name}</div>
                      {item.ageGroups && (
                        <span className="ml-2 text-sm text-gray-400">{item.ageGroups}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-primary-100 text-primary-800">
                      {item.documentCount}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EntityDocumentsList;
